const ANALYTICS_QUEUE_KEY = 'radious:analytics:delivery_queue';
const ANALYTICS_QUEUE_VERSION = 1;
export const ANALYTICS_QUEUE_MAX_EVENTS = 100;
export const ANALYTICS_QUEUE_MAX_BYTES = 256 * 1024;
export const ANALYTICS_QUARANTINE_MAX_EVENTS = 25;
const ANALYTICS_QUARANTINE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type QueueStorage = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
};

export type AnalyticsQueueState<Event> = {
  pending: Array<{ event: Event; attempts: number }>;
  quarantine: Event[];
  singleEventFlushes: number;
};

type PersistedAnalyticsQueue<Event> = AnalyticsQueueState<Event> & {
  version: typeof ANALYTICS_QUEUE_VERSION;
};

const emptyQueue = <Event>(): AnalyticsQueueState<Event> => ({
  pending: [],
  quarantine: [],
  singleEventFlushes: 0,
});

function isQueueEvent(value: unknown): value is { idempotency_key: string; occurred_at: string } {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.idempotency_key === 'string' &&
    candidate.idempotency_key.length > 0 &&
    typeof candidate.occurred_at === 'string'
  );
}

function normalizeState<Event>(state: AnalyticsQueueState<Event>): AnalyticsQueueState<Event> {
  const seen = new Set<string>();
  const pending = state.pending
    .filter(({ event }) => isQueueEvent(event))
    .filter(({ event }) => {
      const key = (event as { idempotency_key: string }).idempotency_key;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(-ANALYTICS_QUEUE_MAX_EVENTS)
    .map(({ event, attempts }) => ({
      event,
      attempts: Number.isInteger(attempts) && attempts > 0 ? attempts : 1,
    }));
  const quarantine = state.quarantine
    .filter(isQueueEvent)
    .filter((event) => {
      const occurredAt = Date.parse((event as { occurred_at: string }).occurred_at);
      return Number.isFinite(occurredAt) && occurredAt >= Date.now() - ANALYTICS_QUARANTINE_MAX_AGE_MS;
    })
    .filter((event) => {
      const key = (event as { idempotency_key: string }).idempotency_key;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(-ANALYTICS_QUARANTINE_MAX_EVENTS) as Event[];
  return {
    pending,
    quarantine,
    singleEventFlushes: Math.min(
      pending.length,
      Number.isInteger(state.singleEventFlushes) && state.singleEventFlushes > 0
        ? state.singleEventFlushes
        : 0
    ),
  };
}

export function boundAnalyticsQueueState<Event>(
  state: AnalyticsQueueState<Event>
): AnalyticsQueueState<Event> {
  const normalized = normalizeState(state);
  const record: PersistedAnalyticsQueue<Event> = {
    version: ANALYTICS_QUEUE_VERSION,
    ...normalized,
  };
  while (
    new TextEncoder().encode(JSON.stringify(record)).byteLength > ANALYTICS_QUEUE_MAX_BYTES &&
    (record.pending.length > 0 || record.quarantine.length > 0)
  ) {
    if (record.quarantine.length > 0) record.quarantine.shift();
    else record.pending.shift();
  }
  record.singleEventFlushes = Math.min(record.singleEventFlushes, record.pending.length);
  return {
    pending: record.pending,
    quarantine: record.quarantine,
    singleEventFlushes: record.singleEventFlushes,
  };
}

export class AnalyticsQueueStorage<Event> {
  private writeChain = Promise.resolve();

  constructor(private readonly storage: QueueStorage) {}

  async restore(): Promise<AnalyticsQueueState<Event>> {
    let value: string | null;
    try {
      value = await this.storage.getItem(ANALYTICS_QUEUE_KEY);
    } catch {
      console.warn('Analytics queue could not be restored.');
      return emptyQueue();
    }
    if (!value) return emptyQueue();
    try {
      const parsed = JSON.parse(value) as Partial<PersistedAnalyticsQueue<Event>>;
      if (
        parsed.version !== ANALYTICS_QUEUE_VERSION ||
        !Array.isArray(parsed.pending) ||
        !Array.isArray(parsed.quarantine) ||
        typeof parsed.singleEventFlushes !== 'number'
      ) {
        await this.discardInvalidRecord();
        return emptyQueue();
      }
      return normalizeState({
        pending: parsed.pending,
        quarantine: parsed.quarantine,
        singleEventFlushes: parsed.singleEventFlushes,
      });
    } catch {
      await this.discardInvalidRecord();
      return emptyQueue();
    }
  }

  save(state: AnalyticsQueueState<Event>): Promise<void> {
    const bounded = boundAnalyticsQueueState(state);
    const serialized = JSON.stringify({ version: ANALYTICS_QUEUE_VERSION, ...bounded });
    this.writeChain = this.writeChain
      .catch(() => undefined)
      .then(() => this.storage.setItem(ANALYTICS_QUEUE_KEY, serialized))
      .catch(() => {
        console.warn('Analytics queue could not be persisted.');
      });
    return this.writeChain;
  }

  private async discardInvalidRecord() {
    console.warn('Invalid analytics queue record discarded.');
    try {
      await this.storage.removeItem(ANALYTICS_QUEUE_KEY);
    } catch {
      console.warn('Analytics queue record could not be discarded.');
    }
  }
}
